// mantine
import {
    Button,
    TagsInput,
    Textarea,
    TextInput,
    UnstyledButton
} from '@mantine/core';

// utils
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useState } from 'react';
import { processFormData } from '@newo/toolkit/bff';
import type { ITopic, ITopicData } from '@newo/api/gateway';
import validationSchema from '@/pages/Workflow/components/StepForm/StepForm.schema.ts';
import {
    useCreateTopicMutation,
    useUpdateTopicMutation
} from '@/services/akb/akb.queries.ts';

interface StepFormProps {
    name?: string;
    labels?: string[];
    topic?: ITopic;
    personaId?: string;
    onClose?: () => void;
}

const DEFAULT_CONFIDENCE = 100;

const StepForm = ({
    name,
    labels,
    personaId,
    topic,
    onClose
}: StepFormProps) => {
    const [searchValue, setSearchValue] = useState('');
    const queryClient = useQueryClient();
    const mutationAdd = useCreateTopicMutation();
    const mutationUpdate = useUpdateTopicMutation();

    const initialValues: ITopicData = {
        topic_name: name ?? topic?.topic_name ?? '',
        persona_id: personaId ?? topic?.persona_id ?? '',
        topic_summary: topic?.topic_summary ?? '',
        topic_facts: topic?.topic_facts ?? [],
        confidence: topic?.confidence ?? DEFAULT_CONFIDENCE,
        source: topic?.source ?? '',
        labels: labels ?? topic?.labels ?? []
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const data = processFormData({
                ...formData,
                labels: searchValue
                    ? [...formData.labels, searchValue]
                    : formData.labels
            });
            if (topic) {
                mutationUpdate.mutate(
                    {
                        topicId: topic.topic_id,
                        data
                    },
                    {
                        onSuccess() {
                            queryClient.invalidateQueries({
                                queryKey: ['akb/topics']
                            });
                            onClose?.();
                        },
                        onSettled() {
                            formik.setSubmitting(false);
                        }
                    }
                );
                return;
            }
            mutationAdd.mutate(data, {
                onSuccess() {
                    queryClient.invalidateQueries({
                        queryKey: ['akb/topics']
                    });
                    onClose?.();
                },
                onSettled() {
                    formik.setSubmitting(false);
                }
            });
        }
    });

    const handleLabelsChange = (value: string[]) => {
        formik.setFieldValue('labels', value);
    };

    const handleCancel = () => {
        onClose?.();
    };

    return (
        <>
            {topic ? (
                <>
                    <div className="text-xs text-gray-6">
                        Created at: {topic.created_at}
                    </div>
                    <div className="text-xs text-gray-6">
                        Updated at: {topic.updated_at}
                    </div>
                </>
            ) : undefined}
            <form
                autoComplete="off"
                className="flex flex-col gap-3"
                onSubmit={formik.handleSubmit}
            >
                <TextInput
                    disabled={!!name}
                    error={
                        formik.touched.topic_name
                            ? formik.errors.topic_name
                            : undefined
                    }
                    label="Name"
                    name="topic_name"
                    placeholder="Topic name"
                    size="sm"
                    value={formik.values.topic_name}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />

                <Textarea
                    autosize
                    error={
                        formik.touched.topic_summary
                            ? formik.errors.topic_summary
                            : undefined
                    }
                    label="Summary"
                    maxRows={40}
                    minRows={15}
                    name="topic_summary"
                    placeholder="Topic summary"
                    size="sm"
                    value={formik.values.topic_summary}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />

                <TagsInput
                    description="Press Enter to submit a label"
                    label="Labels"
                    placeholder="Enter label"
                    searchValue={searchValue}
                    size="sm"
                    value={formik.values.labels}
                    onChange={handleLabelsChange}
                    onSearchChange={setSearchValue}
                />

                <div className="flex justify-between gap-2">
                    <UnstyledButton
                        className="text-xs text-gray-6"
                        onClick={handleCancel}
                    >
                        Cancel
                    </UnstyledButton>
                    <Button
                        disabled={
                            (!formik.dirty && !searchValue) ||
                            !formik.isValid ||
                            formik.isSubmitting
                        }
                        size="xs"
                        type="submit"
                    >
                        Update
                    </Button>
                </div>
            </form>
        </>
    );
};

export default StepForm;
